import _ from 'lodash'
import type { SiteScopeParams, ViewerClientSpecMap } from '@wix/thunderbolt-symbols'
import type {
	Data,
	Logger,
	PageJson,
	BeckyTopology,
	TPAWidgetDataItem,
	WidgetRefDataItem,
	BaseDataItem
} from '@wix/thunderbolt-becky-types'
import type {
	BeckyRequestPayload,
	WidgetTemplatesStore,
	WidgetTemplatesStoreByViewMode,
	BeckyFunctionLibrary
} from '@wix/thunderbolt-becky-root'
import { flattenStructure, deStringifyExperiments } from '@wix/thunderbolt-becky-root'
import { REMOTE_REF_DEAD_COMP } from './constants'
import { getRefTemplateKey } from './byRefsis/byRefUtils'
import type { remoteRefsFetcherParams } from './types'
import { parseAndValidateAppDefinitionIdToSiteRevision } from './validationUtils'
import { pickTopologyAccordingToEnv } from '@wix/thunderbolt-commons'

export const RemoteWidgetStructureBuilderPath = 'pages/pages/remoteWidgetStructureBuilder'

const PATTERN = {
	RECURSIVE: 'recursive',
	ITERATIVE: 'iterative',
}

const REQUEST_PARAMS = [
	'isEditorMode',
	'viewMode',
	'ck',
	'experiments',
	'isHttps',
	'isUrlMigrated',
	'quickActionsMenuEnabled',
	'v',
	'dfVersion',
	'oneDocEnabled',
]
const MODULE_NAME = 'remote-widget-structure-builder'

const objToQueryParams = (obj: Record<string, string>) => {
	return Object.keys(obj)
		.map((key) => `${key}=${obj[key]}`)
		.join('&')
}

export const extractSiteAssetsParams = (str: string) =>
	_.chain(str)
		.split('?')
		.last()
		.split('&')
		.map((x) => x.replace('}', '').replace('{', '').split('='))
		.fromPairs()
		.value()

const extractSiteAssetsParamsFromPayload = (requestPayload: BeckyRequestPayload) => {
	return {
		siteId: requestPayload.siteId,
		metaSiteId: requestPayload.metaSiteId,
		siteRevision: requestPayload.siteRevision,
	}
}

// TODO this function does a lot of work per widget url built where in reality it could do it once per site - we should offload the work to createRemoteRefFetcher once byRefV2 is merged
export const buildWidgetUrl = (
	widgetDataItem: WidgetRefDataItem,
	clientSpecMap: ViewerClientSpecMap,
	beckyTopology: BeckyTopology,
	appDefinitionIdToSiteRevision: SiteScopeParams['appDefinitionIdToSiteRevision'] | null,
	requestPayload: BeckyRequestPayload,
	keepWixCodeConnections: string,
	migrateStylableComponentsInBlocksWidgets: string,
	includeNicknameToIdMap: boolean = false
) => {
	const { appDefinitionId, widgetId, variationId = '' } = widgetDataItem
	const appData = _.find(clientSpecMap, { appDefinitionId })
	const remoteWidgetPageId =
		_.get(appData, ['widgets', widgetId, 'componentFields', 'appStudioFields', 'id']) ||
		requestPayload.widgetIdToPageIdMap?.[widgetDataItem.widgetId]

	const siteAssetsData = _.get(appData, ['appFields', 'platform', 'baseUrls', 'siteAssets'])
	const { siteId, metaSiteId, siteRevision: siteRevisionFromRoot } = siteAssetsData
		? extractSiteAssetsParams(siteAssetsData)
		: extractSiteAssetsParamsFromPayload(requestPayload)

	const siteRevision = appDefinitionIdToSiteRevision
		? _.get(appDefinitionIdToSiteRevision, [appDefinitionId], siteRevisionFromRoot)
		: siteRevisionFromRoot

	const requestParams = _.pick(requestPayload, REQUEST_PARAMS) as Record<string, string>
	const params = {
		appDefId: appDefinitionId,
		devCenterWidgetId: widgetId,
		variationId,
		pageCompId: variationId ? variationId : remoteWidgetPageId,
		siteId,
		metaSiteId,
		siteRevision,
		module: MODULE_NAME,
		moduleVersion: requestPayload.remoteWidgetStructureBuilderVersion,
		keepWixCodeConnections,
		migrateStylableComponents: migrateStylableComponentsInBlocksWidgets,
		...(includeNicknameToIdMap && { includeNicknameToIdMap: 'true' }),
		...requestParams,
	}
	const siteAssetsServerUrl = pickTopologyAccordingToEnv(beckyTopology).siteAssetsServerUrl
	return `${siteAssetsServerUrl}/${RemoteWidgetStructureBuilderPath}?${objToQueryParams(params)}`
}

const safeParseJson = (val?: string) => {
	if (!val) {
		return val
	}

	try {
		return JSON.parse(val)
	} catch (e) {
		if (process.env.NODE_ENV === 'development') {
			console.warn('parseJSON failed parsing value:', val)
		}
		return null
	}
}

type FetchRemoteRef = (widgetDataItem: WidgetRefDataItem) => Promise<WidgetTemplatesStore[string]>

const isTPAWidgetDataItem = (dataItem: BaseDataItem): dataItem is TPAWidgetDataItem => dataItem.type === 'TPAWidget'

const applyApplicationId = (data: PageJson['data'], appDefinitionIdToApplicationId: Record<string, string>) =>
	_.forEach(data.document_data, (dataItem) => {
		if (isTPAWidgetDataItem(dataItem)) {
			dataItem.applicationId = `${appDefinitionIdToApplicationId[dataItem.appDefinitionId] ?? ''}`
		}
	})

const getAppFromSpecMap = (clientSpecMap: ViewerClientSpecMap, appDefinitionId: string) => {
	return _.find(clientSpecMap, (csmEntry) => appDefinitionId === csmEntry.appDefinitionId)
}

const createRemoteRefFetcher = (
	fetchJSON: <Json extends {} = {}>(url: string) => Promise<Json | null>,
	clientSpecMap: ViewerClientSpecMap,
	beckyTopology: BeckyTopology,
	requestPayload: BeckyRequestPayload,
	isMigrateStylableComponentsInBlocksWidgetsExpOn?: boolean,
	logger?: Logger,
	shouldFetchNicknamesMap?: boolean
): FetchRemoteRef => {
	const appDefinitionIdToSiteRevision:
		| SiteScopeParams['appDefinitionIdToSiteRevision']
		| null = requestPayload.appDefinitionIdToSiteRevision
			? parseAndValidateAppDefinitionIdToSiteRevision(requestPayload.appDefinitionIdToSiteRevision)
			: null

	const appDefinitionIdToApplicationId: Record<string, string> = _(clientSpecMap)
		.mapKeys('appDefinitionId')
		.mapValues('applicationId')
		.value()

	return async (
		widgetDataItem: WidgetRefDataItem,
		keepWixCodeConnections: boolean = false
	): Promise<WidgetTemplatesStore[string]> => {
		const app = getAppFromSpecMap(clientSpecMap, widgetDataItem.appDefinitionId)
		const hasPageId =
			_.get(app, ['widgets', widgetDataItem.widgetId, 'componentFields', 'appStudioFields', 'id']) ||
			requestPayload.widgetIdToPageIdMap?.[widgetDataItem.widgetId]
		if (!hasPageId) {
			// @ts-expect-error TODO fix dead ref type
			return REMOTE_REF_DEAD_COMP
		}
		const widgetUrl = buildWidgetUrl(
			widgetDataItem,
			clientSpecMap,
			beckyTopology,
			appDefinitionIdToSiteRevision,
			requestPayload,
			keepWixCodeConnections.toString(),
			(isMigrateStylableComponentsInBlocksWidgetsExpOn ?? false).toString(),
			requestPayload.module?.startsWith('thunderbolt-builder-component') || shouldFetchNicknamesMap
		)
		const widgetJson = await fetchJSON<PageJson>(widgetUrl)
		if (!widgetJson) {
			logger?.error(`fetch ref component failed for ${widgetUrl}`, { widgetDataItem })
			requestPayload.setNotCacheableResponse?.()
			// @ts-expect-error TODO fix dead ref type
			return REMOTE_REF_DEAD_COMP
		}

		// TODO is isMobile always false?!? in carmi we check root.get('viewMode').eq('')
		const isMobile = false

		const { data, structure, translations, compByNickname } = widgetJson
		const dontFilterSlots = requestPayload.isBuilderComponentModel == 'true' || !!deStringifyExperiments(requestPayload.beckyExperiments)['specs.thunderbolt.dontFilterSlots']

		applyApplicationId(data, appDefinitionIdToApplicationId)

		return {
			data,
			structure: flattenStructure(structure, isMobile, data.slots, dontFilterSlots),
			translations,
			rootId: structure.id,
			...(compByNickname && { nicknameToCompIdMap: { [widgetDataItem.widgetId]: compByNickname } }),
		}
	}
}

const createAsyncJsonFetcher = (fetchUrl: BeckyFunctionLibrary['fetchUrl']) => {
	const ERROR = 'ERROR'

	return async <Ret = never>(url: string): Promise<Ret | null> => {
		const svg = await fetchUrl(url, ERROR)
		return svg === ERROR ? null : safeParseJson(svg)
	}
}
const createWidgetRefDataItem = (appDefinitionId: string, widgetId: string): WidgetRefDataItem => ({
	appDefinitionId,
	widgetId,
	type: 'WidgetRef',
	id: '',
})

const getNestedWidgets = (clientSpecMap: ViewerClientSpecMap, widgetDataItem: WidgetRefDataItem) => {
	const { appDefinitionId, widgetId } = widgetDataItem
	const app = _(clientSpecMap).find(['appDefinitionId', appDefinitionId])
	if (!app) {
		return {
			pattern: PATTERN.RECURSIVE,
			widgets: [widgetDataItem],
		}
	}
	const { widgets } = app
	const nestedWidgets = _.get(app.widgets[widgetId], [
		'componentFields',
		'appStudioFields',
		'nestedWidgets',
		'internal',
	])

	const variations = _.get(app.widgets[widgetId], ['componentFields', 'appStudioFields', 'variations'])
	if (!nestedWidgets?.length || !_.isEmpty(variations)) {
		return {
			pattern: PATTERN.RECURSIVE,
			widgets: [widgetDataItem],
		}
	}

	const filteredUnusedWidgets = _.filter(
		widgets,
		(widget) => nestedWidgets.includes(widget.widgetId) || widget.widgetId === widgetId
	)

	return {
		pattern: PATTERN.ITERATIVE,
		widgets: _(filteredUnusedWidgets)
			.map((widget) => createWidgetRefDataItem(appDefinitionId, widget.widgetId))
			.uniqBy(getRefTemplateKey)
			.value(),
	}
}
export const createRemoteRefsFetcher = (params: remoteRefsFetcherParams) => {
	const {
		fetchUrl,
		clientSpecMap,
		beckyTopology,
		requestPayload,
		isMigrateStylableComponentsInBlocksWidgetsExpOn,
		logger,
		shouldFetchNicknamesMap,
		getTemplateKeyByViewMode = false,
		cache,
	} = params

	const fetchRemoteRef = createRemoteRefFetcher(
		createAsyncJsonFetcher(fetchUrl),
		clientSpecMap,
		beckyTopology,
		requestPayload,
		isMigrateStylableComponentsInBlocksWidgetsExpOn,
		logger,
		shouldFetchNicknamesMap
	)
	const viewMode = requestPayload.viewMode!
	const viewModeForTemplateKey = getTemplateKeyByViewMode ? viewMode : undefined
	const widgets: WidgetTemplatesStoreByViewMode = { desktop: {}, mobile: {} }

	const fetchAndSave = async (widgetDataItem: WidgetRefDataItem) => {
		const widgetKey = getRefTemplateKey(widgetDataItem, viewModeForTemplateKey)
		if (cache) {
			cache[widgetKey] = true
		}
		const remoteRef = await fetchRemoteRef(widgetDataItem)
		widgets[viewMode][widgetKey] = remoteRef
		return remoteRef
	}

	const getWidgetDataItems = (data: Data['document_data']) =>
		_(data)
			.pickBy({ type: 'WidgetRef' })
			.values()
			.uniqBy((widgetDataItem) => getRefTemplateKey(widgetDataItem as WidgetRefDataItem, viewModeForTemplateKey))
			.reject((widgetDataItem) => {
				const templateKey = getRefTemplateKey(widgetDataItem as WidgetRefDataItem, viewModeForTemplateKey)
				return cache?.[templateKey] || !!widgets[viewMode]?.[templateKey]
			})
			.value() as Array<WidgetRefDataItem>

	const fetchRemoteWidgets = async (documentData: Data['document_data']): Promise<WidgetTemplatesStoreByViewMode> => {
		const widgetDataItems = getWidgetDataItems(documentData)

		const fetchWidgetsIteratively = async (widgetsToFetch: Array<WidgetRefDataItem>) => {
			await Promise.all(
				widgetsToFetch.map(async (widget) => {
					const remoteRef = await fetchAndSave(widget)
					const missingWidgets = getWidgetDataItems(remoteRef.data.document_data).filter(
						(wid) => !widgetsToFetch.find((nw) => nw.widgetId === wid.widgetId)
					)
					// TODO: this is a recursive call, we should consider changing it to iterative once clientSpecMap will countian outer widgets as well
					// Slots inside widgets will not be fetched since they have different appDefId
					if (missingWidgets?.length) {
						await Promise.all(
							missingWidgets.map(async (missingWidget) => {
								const missedWidget = await fetchAndSave(missingWidget)
								return fetchRemoteWidgets(missedWidget.data.document_data)
							})
						)
					}
				})
			)
		}

		const fetchWidgetsRecursively = async (widgetsToFetch: Array<WidgetRefDataItem>) => {
			await Promise.all(
				widgetsToFetch.map(async (widgetDataItem) => {
					const widgetKey = getRefTemplateKey(widgetDataItem, viewModeForTemplateKey)

					// @ts-expect-error Mark widget as being downloaded
					widgets[viewMode][widgetKey] = null
					const remoteRef = await fetchAndSave(widgetDataItem)

					return fetchRemoteWidgets(remoteRef.data.document_data)
				})
			)
		}

		const nestedWidgets = _.map(widgetDataItems, (widgetDataItem: WidgetRefDataItem) =>
			getNestedWidgets(clientSpecMap, widgetDataItem)
		)
		const groupWidgetsByPattern = _.reduce(
			nestedWidgets,
			(result, value) => {
				if (value) {
					const { pattern, widgets: widgetsToFetch } = value
					result[pattern] = result[pattern].concat(widgetsToFetch)
				}
				return result
			},
			{ [PATTERN.ITERATIVE]: [], [PATTERN.RECURSIVE]: [] } as Record<string, Array<WidgetRefDataItem>>
		)
		await fetchWidgetsRecursively(groupWidgetsByPattern[PATTERN.RECURSIVE])
		await fetchWidgetsIteratively(groupWidgetsByPattern[PATTERN.ITERATIVE])

		return widgets
	}

	return fetchRemoteWidgets
}
